import React from "react";
import { Card, Col } from "react-bootstrap";

export type StatCardColor =
  | "primary"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "secondary"
  | "dark";

interface StatCardProps {
  title: string;
  value: string;
  icon: string;
  color: StatCardColor;
  valueClassName?: string;
  subtext?: string;
  change?: number;
  changeType?: "positive" | "negative" | "neutral";
  layout?: "default" | "name";
  fullWidth?: boolean;
}

const ICON_BG: Record<StatCardColor, React.CSSProperties> = {
  primary: { background: "rgba(13, 110, 253, 0.10)" },
  success: { background: "rgba(25, 135, 84, 0.10)" },
  warning: { background: "rgba(253, 126, 20, 0.10)" },
  danger: { background: "rgba(220, 53, 69, 0.10)" },
  info: { background: "rgba(13, 202, 240, 0.10)" },
  secondary: { background: "rgba(108, 117, 125, 0.10)" },
  dark: { background: "rgba(33, 37, 41, 0.10)" },
};

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  color,
  valueClassName,
  subtext,
  change,
  changeType = "neutral",
  layout = "default",
  fullWidth = false,
}) => {
  const showChange =
    changeType !== "neutral" && typeof change === "number" && change !== 0;

  const iconBlockStyle: React.CSSProperties = {
    fontSize: "2.25rem",
    width: 56,
    height: 56,
    borderRadius: "12px",
    flexShrink: 0,
    ...ICON_BG[color],
  };

  return (
    <Col xs={fullWidth ? 12 : undefined} md={fullWidth ? 12 : 6} lg={fullWidth ? 12 : 4} xl={fullWidth ? 12 : 4} className="mb-3">
      <Card className={`stats-card ${color} h-100`}>
        <Card.Body className="d-flex flex-column h-100">
          {layout === "name" ? (
            <>
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div className="stats-label">{title}</div>
                <div
                  className={`text-${color} d-flex align-items-center justify-content-center ms-2`}
                  style={iconBlockStyle}
                >
                  <i className={icon} />
                </div>
              </div>
              <div className="stats-name-value" title={value}>
                {value}
              </div>
              {subtext ? <div className="stats-subtext">{subtext}</div> : null}
            </>
          ) : (
            <div className="d-flex justify-content-between align-items-start flex-grow-1">
              <div className="flex-grow-1 overflow-hidden">
                <div className="stats-label">{title}</div>
                <div
                  className={`stats-number text-truncate ${valueClassName ?? ""}`}
                  title={value}
                >
                  {value}
                </div>
                {subtext ? <div className="stats-subtext">{subtext}</div> : null}
                {showChange ? (
                  <div
                    className={`stats-change ${changeType === "positive" ? "text-success" : "text-danger"}`}
                  >
                    <i
                      className={`bi-arrow-${changeType === "positive" ? "up" : "down"}-right me-1`}
                    />
                    {Math.abs(change as number).toFixed(1)}%
                  </div>
                ) : null}
              </div>
              <div
                className={`text-${color} ms-2 d-flex align-items-center justify-content-center`}
                style={iconBlockStyle}
              >
                <i className={icon} />
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

export default StatCard;
